import React, { Children } from 'react';
import { motion } from 'framer-motion';
const ACCENT = '#3B4A4F';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
type SectionProps = {
  title: string;
  children: React.ReactNode;
};
function Section({
  title,
  children
}: SectionProps) {
  return <motion.section variants={fadeUp} className="mb-12" data-id="element-2746">
      <h2 className="font-serif text-2xl md:text-3xl text-[#1F1F1F] mb-5 leading-[1.2]" data-id="element-2747">
        {title}
      </h2>
      <div className="space-y-4 text-[15px] text-[#3A3A33] leading-relaxed" data-id="element-2748">
        {children}
      </div>
    </motion.section>;
}
export function Terms() {
  const lastUpdated = 'May 2026';
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-2749">
      {/* HERO */}
      <section className="pt-32 md:pt-36 pb-12 md:pb-16 px-6 md:px-10 border-b border-[#E5E5E5]" data-id="element-2750">
        <div className="max-w-3xl mx-auto" data-id="element-2751">
          <motion.div initial="hidden" animate="visible" variants={stagger} data-id="element-2752">
            <motion.p variants={fadeUp} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-6" style={{
            color: ACCENT
          }} data-id="element-2753">
              Legal
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-serif text-4xl md:text-5xl text-[#1F1F1F] leading-[1.05] mb-5" data-id="element-2754">
              Terms of Service
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm text-[#5A5A50]" data-id="element-2755">
              Last updated: {lastUpdated}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-16 md:py-20 px-6 md:px-10" data-id="element-2756">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-3xl mx-auto" data-id="element-2757">
          <motion.p variants={fadeUp} className="text-[15px] text-[#3A3A33] leading-relaxed mb-12" data-id="element-2758">
            These Terms of Service ("Terms") govern your access to and use of
            the Structured Nutrition website, products, and services
            ("Services"). By using our Services, you agree to these Terms. If
            you do not agree, please do not use the Services.
          </motion.p>

          {/* DISCLAIMERS — two clearly separated callouts */}
          <motion.div variants={fadeUp} className="mb-6 p-6 md:p-8 border-l-4" style={{
          backgroundColor: '#ECEDE2',
          borderColor: ACCENT
        }} data-id="element-2759">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-3" style={{
            color: ACCENT
          }} data-id="element-2760">
              General Content — Educational Only
            </p>
            <p className="text-[15px] text-[#1F1F1F] leading-relaxed" data-id="element-2761">
              The general content published on this website, in our books,
              guides, downloads, emails, and social media is for educational and
              informational purposes only. Because we don't know your individual
              medical history, medications, or circumstances, this content is
              not personalized nutrition advice and is not a substitute for
              consulting a licensed healthcare provider. Always speak with your
              prescribing physician before making changes to your medication,
              nutrition, or exercise plan — especially while taking GLP-1
              medications. Never disregard professional medical advice or delay
              seeking it because of something you read here.
            </p>
          </motion.div>

          <motion.div variants={fadeUp} className="mb-12 p-6 md:p-8 border-l-4" style={{
          backgroundColor: '#ECEDE2',
          borderColor: ACCENT
        }} data-id="element-2762">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-3" style={{
            color: ACCENT
          }} data-id="element-2763">
              1:1 Coaching — Professional Dietetic Services
            </p>
            <p className="text-[15px] text-[#1F1F1F] leading-relaxed" data-id="element-2764">
              One-on-one nutrition coaching with Structured Nutrition is
              provided by a Registered Dietitian Nutritionist (RDN) and consists
              of medical nutrition therapy and individualized nutrition
              counseling within the scope of practice of a credentialed
              dietitian. These services complement, but do not replace, the care
              of your prescribing physician or other healthcare providers. We do
              not diagnose disease, prescribe medication, or adjust prescription
              dosing — those decisions remain with your physician. Clients are
              responsible for keeping their medical team informed about the
              nutrition plan we develop together.
            </p>
          </motion.div>

          <Section title="Our Services" data-id="element-2765">
            <p data-id="element-2766">
              Structured Nutrition offers nutrition coaching, digital products
              (e-books, downloadable guides), training programs, and related
              educational content. Service descriptions, availability, and
              pricing may change without notice.
            </p>
          </Section>

          <Section title="Accounts" data-id="element-2767">
            <p data-id="element-2768">
              To access certain features, you'll need to create an account. You
              agree to:
            </p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2769">
              <li data-id="element-2770">Provide accurate and complete information</li>
              <li data-id="element-2771">Keep your login credentials confidential</li>
              <li data-id="element-2772">
                Be responsible for all activity that happens under your account
              </li>
              <li data-id="element-2773">
                Notify us immediately of any unauthorized use of your account
              </li>
            </ul>
            <p data-id="element-2774">
              We may suspend or terminate accounts that violate these Terms.
            </p>
          </Section>

          <Section title="Purchases and Payment" data-id="element-2775">
            <p data-id="element-2776">
              Payments are processed securely by Stripe. By purchasing a product
              or service, you authorize us to charge the payment method you
              provide for the full purchase amount, including any applicable
              taxes.
            </p>
            <p data-id="element-2777">
              All prices are listed in U.S. dollars unless otherwise noted. We
              reserve the right to change pricing at any time.
            </p>
          </Section>

          <Section title="Refunds" data-id="element-2778">
            <p data-id="element-2779">
              Digital products (e-books, guides, downloads) are non-refundable
              once accessed or downloaded. Coaching services may be eligible for
              a refund within 7 days of purchase if no session has yet been
              delivered — contact us to request one. Refunds for ongoing
              programs are evaluated case-by-case.
            </p>
          </Section>

          <Section title="Intellectual Property" data-id="element-2780">
            <p data-id="element-2781">
              All content on this site — including text, graphics, logos, books,
              guides, and training materials — is owned by Structured Nutrition
              or licensed to us and is protected by copyright and other
              intellectual property laws.
            </p>
            <p data-id="element-2782">
              You may not copy, distribute, resell, or create derivative works
              from our content without our prior written permission. Purchasing
              a digital product grants you a personal, non-transferable license
              to use it.
            </p>
          </Section>

          <Section title="Acceptable Use" data-id="element-2783">
            <p data-id="element-2784">By using our Services, you agree NOT to:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2785">
              <li data-id="element-2786">
                Use the Services for any unlawful, harmful, or fraudulent
                purpose
              </li>
              <li data-id="element-2787">
                Share, redistribute, or resell our digital products or coaching
                materials
              </li>
              <li data-id="element-2788">
                Attempt to gain unauthorized access to our systems or other
                users' accounts
              </li>
              <li data-id="element-2789">Harass, abuse, or threaten our team or other users</li>
              <li data-id="element-2790">
                Use our content to provide nutrition or medical advice to others
                as a substitute for your own credentialed practice
              </li>
            </ul>
          </Section>

          <Section title="Disclaimer of Warranties" data-id="element-2791">
            <p data-id="element-2792">
              Our Services are provided "as is" and "as available," without
              warranties of any kind, either express or implied. We do not
              guarantee any specific health, weight, or fitness outcomes from
              using our Services.
            </p>
          </Section>

          <Section title="Limitation of Liability" data-id="element-2793">
            <p data-id="element-2794">
              To the fullest extent permitted by law, Structured Nutrition and
              its team are not liable for any indirect, incidental, special,
              consequential, or punitive damages arising from your use of the
              Services — including but not limited to health outcomes, lost
              profits, or data loss.
            </p>
            <p data-id="element-2795">
              Our total liability for any claim arising from these Terms or your
              use of the Services is limited to the amount you paid us in the 12
              months preceding the event giving rise to the claim.
            </p>
          </Section>

          <Section title="Indemnification" data-id="element-2796">
            <p data-id="element-2797">
              You agree to defend, indemnify, and hold harmless Structured
              Nutrition from any claims, damages, or expenses (including
              reasonable attorneys' fees) arising out of your violation of these
              Terms or your misuse of the Services.
            </p>
          </Section>

          <Section title="Governing Law" data-id="element-2798">
            <p data-id="element-2799">
              These Terms are governed by the laws of the State of [Your State],
              without regard to conflict-of-laws principles. Any dispute will be
              resolved in the state or federal courts located in [Your
              County/State].
            </p>
          </Section>

          <Section title="Changes to These Terms" data-id="element-2800">
            <p data-id="element-2801">
              We may update these Terms from time to time. The "Last updated"
              date at the top reflects the most recent revision. Continued use
              of the Services after changes constitutes acceptance of the
              updated Terms.
            </p>
          </Section>

          <Section title="Contact Us" data-id="element-2802">
            <p data-id="element-2803">
              Questions about these Terms? Reach out through our{' '}
              <a href="/contact" className="underline hover:no-underline" style={{
              color: ACCENT
            }} data-id="element-2804">
                contact page
              </a>
              .
            </p>
          </Section>

          <motion.p variants={fadeUp} className="text-xs text-[#5A5A50] italic mt-16 pt-8 border-t border-[#E5E5E5]" data-id="element-2805">
            This document is provided as a general template and does not
            constitute legal advice. Bracketed placeholders (such as state and
            venue) should be filled in, and the full document should be reviewed
            with an attorney before being relied upon.
          </motion.p>
        </motion.div>
      </section>
    </div>;
}